import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { devLog } from "@/lib/devLog";

/**
 * Anonymous session-id kept in sessionStorage for the tab's lifetime.
 * Falls back to a random UUID when crypto.randomUUID is unavailable.
 */
function getSessionId() {
  try {
    const k = "ctp_analytics_sid";
    let sid = sessionStorage.getItem(k);
    if (!sid) {
      sid = (window.crypto && window.crypto.randomUUID)
        ? window.crypto.randomUUID()
        : `sid-${Date.now()}-${Math.random().toString(36).slice(2)}`;
      sessionStorage.setItem(k, sid);
    }
    return sid;
  } catch (err) {
    devLog.debug("analytics sessionStorage unavailable", err);
    return `sid-${Date.now()}-${Math.random().toString(36).slice(2)}`;
  }
}

/**
 * Fires a heartbeat every 45 s while the tab is open (visible only), polls
 * the live-users count every 45 s, and returns the current count for UI use.
 *
 * Nothing is stored beyond the anonymous session id — no PII, no cookies,
 * TTL-purged after 10 min on the backend.
 */
export function useLiveUsers({ path } = {}) {
  const [count, setCount] = useState(0);
  const [exact, setExact] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const sid = getSessionId();

    const beat = async () => {
      if (document.hidden) return;
      try {
        await api.post("/analytics/heartbeat", { session_id: sid, path: path || window.location.pathname });
      } catch (err) {
        // Analytics is best-effort — never surface to the user, log for devs.
        devLog.debug("analytics heartbeat failed", err);
      }
    };
    const poll = async () => {
      try {
        const { data } = await api.get("/analytics/live-users");
        if (!cancelled) {
          setCount(Number(data?.live_users || 0));
          setExact(Boolean(data?.exact));
        }
      } catch (err) {
        devLog.debug("analytics live-users poll failed", err);
      }
    };

    beat(); poll();
    const bt = setInterval(beat, 45_000);
    const pt = setInterval(poll, 45_000);
    return () => { cancelled = true; clearInterval(bt); clearInterval(pt); };
  }, [path]);

  return { count, exact };
}

/** Fire-and-forget tutorial click counter. */
export async function trackTutorialClick() {
  try {
    await api.post("/analytics/tutorial-click");
  } catch (err) {
    devLog.debug("tutorial-click track failed", err);
  }
}
